import React from 'react';
import {action, computed, observable, toJS} from 'mobx';
import XDate from 'xdate';
import {evite} from 'evite';
import urls from 'urls';
import {FormStore} from '~/common/form/store';
import {ConfirmDialog} from '~/create/components/ConfirmDialog/index';
import {
  getTimezone,
  valid_email,
  formatTimezoneOffset,
  addParamsToUrl,
  getURLParameter,
  isValidURL,
} from '~/common/utils';
import {PopupModal} from '~/modal/popup_modal';
import {ModalAjaxError} from '../../modal/index';
import {replaceEmpty} from '~/create/components/CardEditor/wordingUtils';
import {UNTITLED_CARD, UNTITLED_EVENT, CARD} from '~/create/constants/index';
import {RSVP_STYLES} from '~/create/constants/rsvpStyles';
import * as utils from '~/view_invitation/utils';

export const PAST_ERROR = 'Please enter a date not in the past';
export const PAST_BEFORE_START_ERROR = 'End Date/Time must be after Start Date/Time';

const UNKNOWN_HOST_PLACEHOLDER = 'Unknown host';

const DIRTY_FIELDS = [
  'title',
  'host_name',
  'start_datetime',
  'end_datetime',
  'phone_number',
  'location_name',
  'street_address',
  'city',
  'state',
  'zip_code',
  'message',
  'custom_image_id',
  'event_option_max_guest_number',
  'event_option_max_guests',
  'event_option_allow_guest_number',
  'event_option_allow_kids',
  'event_option_allow_invite_more',
  'event_option_premium_shareable_link',
  'event_option_show_gifting',
  'event_option_enable_sponsored_registry',
  'gift_list_url',
];

export class FormFieldModel {
  @observable accessor fieldStatus = {
    error: null,
    valid: true,
  };

  validators = [];

  required = false;

  placeholder = '';

  label = '';

  name = '';

  field = '';

  type = 'text';

  fieldTwo = null;

  options = false;

  store = null;

  constructor(modelProps) {
    // setup the initial model that we'll watch for errors
    this.update(modelProps);
  }

  /**
   * props can (and do) change with every render. this model really
   * shouldn't have stored all these values because it then doesn't
   * react to changes. So for now, we'll just break out this function
   * and update the props on every render.
   *
   * @param props
   */
  update = (props) => {
    this.name = props.name;
    this.store = props.store;
    if (!this.store) throw new Error(`${this.name} requires a store property`);
    this.type = props.type || this.type;
    this.field = props.field || this.name;
    this.fieldTwo = props.fieldTwo;
    this.label = props.label || this.label;
    this.placeholder = props.placeholder || this.placeholder;
    this.getValue = props.getValue || this.getValue;
    this.required = props.required || this.required;
    this.validators = props.validators || this.validators;
    if (props.options) {
      // special case for radio buttons with defaultChecked option
      const defaultChecked = props.options.find((option) => option.defaultChecked);
      if (defaultChecked && !this.getValue()) {
        this.setValue(defaultChecked.value);
      }
    }
  };

  getValue = () => {
    const field = this.field.replace(/-/g, '.');
    if (field.includes('.') || field.includes('[')) return this.store.get(field);
    if (this.fieldTwo) return this.store.event[this.fieldTwo][this.field];

    return this.store.event[this.field];
  };

  @action
  setValue = (value) => {
    if (this.fieldTwo) {
      this.store.event[this.fieldTwo][this.field] = value;
      this.store.onFieldChange(this.field, value);
      return;
    }

    if (this.field.includes('.')) {
      this.store.set(this.field, value);
    } else {
      this.store.event[this.field] = value;
    }
    this.store.onFieldChange(this.field, value);
  };

  @computed
  get isValid() {
    return !this.fieldStatus.error;
  }

  /**
   * validate the component. returns true if valid
   * @returns {boolean}
   */
  @action
  validate = (testValue) => {
    const {validators} = this;
    let error = null;
    const value = testValue || this.getValue();
    // hide whole form errors, if present
    this.store.setFormError(null);

    for (const func of validators) {
      error = func(value);
      if (error) break;
    }
    // Show error if the required input values are empty or have only spaces
    if (!error && this.required && !evite.str(value).trim()) {
      error = `${this.label || this.placeholder} is required`;
    }

    if (error) evite.info(`field ${this.name}, error: ${error}`);
    this.fieldStatus.error = error;
    return error === null;
  };
}

const defError = 'Sorry, there was an unknown error while saving. Please try again.';

export class EventFormStore extends FormStore {
  /**
   * if the first nonprimary reminder is visible or not
   * */
  @observable accessor formStatus = {
    saving: false,
    error: null,
    validated: false,
    fields: {},
    isDirty: false,

    reminder2Visible: false,
    cohostVisible: false,
    endTimeVisible: false,
    _isUpgrade: false,
    isLogoEditMode: false,
    registry_name_error: false,
    registry_urls_error: false,
  };
  /**
   * If the form is considered changed and should show a Send Message Modal
   */

  constructor(event, template, widget) {
    super();
    this.template = template;

    this.event = event;
    this.widget = widget;
    this.lastReminderSent = window.lastReminderSent;

    this.isProUpsellEnabled = evite.features && evite.features.isEnabled('evite_pro_upsell_promo');

    evite.injectStores('controller', 'templateStore', 'paymentStore', 'videoStore', this);

    const categoryName = getURLParameter('c');
    if (categoryName && categoryName === 'virtual_events') {
      const {timezoneId} = getTimezone();
      this.event.known_timezone_name = timezoneId;
    }

    const default_host_name = window.user_name;
    if (!this.event.host_name || this.event.host_name === UNKNOWN_HOST_PLACEHOLDER) {
      this.event.host_name = default_host_name;
    }
    if (this.event.cohost_name1 || this.event.cohost_email1) {
      this.addCohost();
    }
    if (this.event.end_datetime) {
      this.addEndTime();
    }
    if (this.event.event_reminder_1) {
      this.addReminder2();
    }
    if (!this.event.registries) {
      this.event.registries = [];
    }
    if (this.event.is_host_viewing === null) {
      this.event.is_host_viewing = true;
    }

    if (!this.event.id) {
      // default (on create) allow rsvp w/ kids if event type is birthday_forkids
      this.event.options.event_option_allow_kids = this.event.event_type === 'birthday_forkids';
    }

    if (window.isPremium) {
      if (!this.event.options.write) this.event.options.write = {};
      if (!this.event.options.write.datetime_style)
        this.event.options.write.datetime_style = 'formal';
      this.event.options.write.wording_type = this.wordingType;
    }

    // event type controls some event options - make sure they are initialized correctly.
    if (!this.event.id) this.willReceiveNewEventType(this.event.event_type);

    // for EVT-19884, we want to know if the user downgraded to a free event
    this.fromPremiumDowngrade = window.fromPremiumDowngrade;

    // "external validators" - functions to be called when
    // doing validation of "our known stuff" to validate
    // their things as well - if any of them returns true,
    // we'll consider our validation to have failed - and
    // assume those external things are showing the user
    // what the problem(s) are...
    this.externalValidators = [];

    // "external data contributors" - functions to be called
    // with the "submission data" before making an AJAX request
    // to the backend, which allows other modules/stores/etc.
    // to contribute to the event data, without EventFormStore
    // having to explicitly know about them all
    this.externalDataContributors = [];

    // ...and on the return side of the AJAX request to the
    // backend with any data contributions made by
    // this.externalDataContributors, also allow external
    // things to update themselves with data from the backend
    // response
    this.externalResponseDataHandlers = [];
  }

  get(str, opt_defaultValue) {
    return evite.get(this, str, opt_defaultValue);
  }

  set(str, value, opt_options) {
    evite.set(this, str, value, {strict: true});
  }

  /**
   * Save the experiment variation in the event options
   * @param experimentName
   * @param variation
   */
  setExperimentVariation = (experimentName, variation) => {
    const expVariation = this.event.options.experiment_variations || {};
    if (!expVariation[variation]) {
      if (!this.event.options.experiment_variations) {
        this.event.options.experiment_variations = {
          [experimentName]: variation,
        };
      } else {
        this.event.options.experiment_variations[experimentName] = variation;
      }
    }
  };

  /**
   * return the correct wishlist or registry type for the current event_id
   */
  @computed
  get giftingType() {
    if (this.event.registries && this.event.registries.length > 0) return 'knot';
    return 'amazon';
  }

  /**
   * return the correct wording type for this event
   */
  @computed
  get wordingType() {
    if (this.event.options && this.event.options.write && this.event.options.write.wording_type) {
      // sometimes it's weird things like 'correspondence' or 'announcement'
      return this.event.options.write.wording_type === 'invitation' ? 'invitation' : 'card';
    }
    return 'invitation';
  }

  /**
   * convert our widget data to submittable js
   */
  getSubmitWidgetData() {
    const {polls, whatToBrings} = this.widget;
    const widgetData = {polls: toJS(polls)};

    // if whatToBrings has default values and user didn't
    // input anything, set as an empty array
    widgetData.whatToBrings =
      whatToBrings.length && !whatToBrings[0].editing ? [] : toJS(whatToBrings);
    return widgetData;
  }

  /**
   * convert our store data to submittable js
   */
  getSubmitData() {
    if (this.controller.isPremium) {
      if (this.controller.step === 'write') {
        // on premium we need to clean up the wording
        let wording = this.event.options.write.text_layer;
        wording = replaceEmpty(wording);
        this.event.options.write.text_layer = wording;
        // For card, we have to set a start_datetime for backend.
        if (this.wordingType === 'card') {
          const start = new XDate().addWeeks(2); // Set start date to two weeks from today
          this.event.start_datetime = start.toString('i').slice(0, 16);
        }
      }

      // and make sure the urls to the envelope and liner are updated
      const env = this.templateStore.getEnvelope();
      this.event.options.design.envelope_flap_down = env.flapDown;
      const {liner} = env;
      this.event.options.design.envelope_liner_image = liner.image;
    }
    if (this.videoStore.video_id) {
      this.event.event_videos_id = this.videoStore.video_id;
    }

    const data = {event: toJS(this.event), widgets: this.getSubmitWidgetData()};

    // just out of paranoia, and because it's happened a few times, let's
    // not change the template name for premium in any case
    // this is something that'll have to be removed with a premium rewrite
    if (!this.controller.isPremium) {
      data.event.template_name = this.template.name;
    }

    // make sure the start date includes the local offset for analytics purposes
    if (data.event.start_datetime) {
      data.event.start_datetime += formatTimezoneOffset();
    }

    this.addToSubmissionDataFromExternalContributors(data);

    return data;
  }

  @action
  addField(formFieldProps) {
    const newField = new FormFieldModel(formFieldProps);
    this.formStatus.fields[formFieldProps.name] = newField;

    return newField;
  }

  @action
  async saveForm(data, action = '') {
    console.log('EVENT FORM SAVE.');

    const {controller} = this;
    await controller.showLoading();
    this.saving = true;

    // if we have an event id, this is an update, regardless of the status
    let url;
    let creating = false;
    if (data.event.id) {
      url = `/ajax/event/${data.event.id}/update`;
    } else {
      creating = true;
      url = '/ajax/event/create';
      if (action) {
        url += `?action=${action}`;
      }
    }

    // IMAGE FINISHING  FLAGS FOR FREE EVENT
    const {anonDyoImageKey, anonStampImageKey, anonLogoImageKey} = controller;

    console.log('data.event.options.custom_image_id......', data.event.options.custom_image_id);

    // Include flags for anon images that need finishing.
    if (anonDyoImageKey || data.event.options.custom_image_id) {
      console.log('ADDING anonDyoImageKey FLAG FOR FINISHING.', anonDyoImageKey);
      data.dyo = anonDyoImageKey || data.event.options.custom_image_id;
    }
    if (anonStampImageKey) {
      console.log('ADDING anonStampImageKey FLAG FOR FINISHING.', anonStampImageKey);
      data.stamp = anonStampImageKey;
      data.event.options.design.custom_stamp_key = anonStampImageKey;
    }
    if (anonLogoImageKey) {
      console.log('ADDING anonLogoImageKey FLAG FOR FINISHING.', anonLogoImageKey);
      data.logo = anonLogoImageKey;
      // If the logo has been removed, data.event.custom_logo might be null now, so check first.
      if (data.event.custom_logo) {
        data.event.custom_logo.image_id = anonLogoImageKey;
      }
    }

    try {
      const response = await evite.fetch(url, {
        method: 'post',
        body: JSON.stringify(data),
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8',
        },
      });

      this.saving = false;
      await controller.hideLoading();
      const responseJSON = await response.json();

      if (!response.ok) {
        if (responseJSON.message) {
          this.setFormError(responseJSON.message);
          throw {silent: true};
        }
        throw new Error(responseJSON.message || 'Unknown error');
      } else if (responseJSON.login) {
        window.location.assign(responseJSON.next);
        throw {silent: true}; // Don't show an error because of page redirect
      } else {
        const whatToBringData =
          responseJSON.widgets && responseJSON.widgets.whatToBrings
            ? responseJSON.widgets.whatToBrings.slice()
            : [];
        // in case user goes back to edit details
        if (whatToBringData.length) {
          whatToBringData[0].editing = true;
        } else {
          whatToBringData.push({editing: false});
        }

        const widgetData = {...responseJSON.widgets, whatToBrings: whatToBringData};

        Object.assign(this.widget, widgetData);
        Object.assign(this.event, responseJSON.event);

        this.sendResponseDataToExternalResponseHandlers(responseJSON);

        if (creating) {
          // the payment store may need to reselect the offer
          this.paymentStore.onEventCreate();
        }

        return responseJSON;
      }
    } catch (e) {
      if (!e.silent) {
        evite.error('Error saving %O', e);
        Raven.captureException(e);
        if (this.saving) {
          this.saving = false;
          await controller.hideLoading();
        }
        await controller.showError('Error', e.message || defError);
      }
      throw e;
    }
  }

  /**
   * validate fields and all of the special cases
   */
  validateForm = () => {
    let valid = true;
    valid = this.validateRsvpStyles() && valid; // needs to go before the field
    valid = this.validateAll() && valid;
    valid = this.validateUyo() && valid;
    valid = this.validateCohost() && valid;
    valid = this.validateRegistries() && valid;
    // this.dispatchEvent(new CustomEvent('validated', {detail: {valid: valid}}));
    valid = this.validateExternal() && valid;
    return valid;
  };

  addExternalValidator(validatorFunc) {
    this.externalValidators.push(validatorFunc);
  }

  validateExternal() {
    const retVals = [];
    this.externalValidators.forEach((validationFunc) => {
      retVals.push(validationFunc());
    });
    // note: each function in externalValidators should return
    // true for "yes, there's a problem" and false
    // for "nope, no issues here"
    const allOkay = !retVals.includes(true);
    return allOkay;
  }

  addExternalDataContributor(dataContribFunc, responseHandlerFunc = null) {
    this.externalDataContributors.push(dataContribFunc);
    if (responseHandlerFunc) this.externalResponseDataHandlers.push(responseHandlerFunc);
  }

  addToSubmissionDataFromExternalContributors(submitData) {
    this.externalDataContributors.forEach((contribFunc) => contribFunc(submitData));
  }

  sendResponseDataToExternalResponseHandlers(responseData) {
    this.externalResponseDataHandlers.forEach((handlerFunc) => handlerFunc(responseData));
  }

  /**
   * Submit this form and save a draft
   *
   * @returns {Promise}
   */
  @action
  saveDraft = (action = '') => {
    const valid = this.validateForm();
    if (!valid) throw 'INVALID_EVENT';

    const data = this.getSubmitData();
    if (data.event) data.event.status = 'draft';
    else data.status = 'draft';
    return this.saveForm(data, action);
  };

  /**
   * Submit this form.
   *
   * @returns {Promise}
   */
  @action
  submit = () => {
    const valid = this.validateForm();
    if (!valid) throw 'INVALID_EVENT';

    const data = this.getSubmitData();
    if (data.event) data.event.status = 'sending';
    else data.status = 'sending';
    return this.saveForm(data);
  };

  /**
   * Create a new premium event
   *
   * Because we don't have a start date or other required information, we save with
   * user_saved=false for these events to start a draft
   */
  createPremium = async () => {
    const {controller} = this;
    await controller.showLoading();
    this.saving = true;

    // if we have an event id, this is an update, regardless of the status
    const url = '/ajax/event/create';
    const data = this.getSubmitData();

    const {anonDyoImageKey, anonStampImageKey, anonLogoImageKey} = controller;

    // Include flags for anon images that need finishing.
    if (anonDyoImageKey) {
      console.log('ADDING anonDyoImageKey FLAG FOR FINISHING.', anonDyoImageKey);
      data.dyo = anonDyoImageKey;
    }
    if (anonStampImageKey) {
      console.log('ADDING anonStampImageKey FLAG FOR FINISHING.', anonStampImageKey);
      data.stamp = anonStampImageKey;
    }
    if (anonLogoImageKey) {
      console.log('ADDING anonLogoImageKey FLAG FOR FINISHING.', anonLogoImageKey);
      data.logo = anonLogoImageKey;
    }

    data.event.user_saved = false;
    data.event.host_name = window.user_name || UNKNOWN_HOST_PLACEHOLDER;
    const start = new XDate().addWeeks(2);
    data.event.start_datetime = start.toString('i').slice(0, 16) + formatTimezoneOffset();
    if (this.wordingType === CARD) {
      data.event.title = UNTITLED_CARD;
    } else {
      data.event.title = UNTITLED_EVENT;
    }

    try {
      const response = await window.evite.fetch(url, {
        method: 'post',
        body: JSON.stringify(data),
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8',
        },
      });

      this.saving = false;
      await controller.hideLoading();
      const responseJSON = await response.json();

      if (!response.ok) {
        throw new Error(responseJSON.message || 'Unknown error');
      } else if (responseJSON.login) {
        window.location.assign(responseJSON.next);
      } else {
        Object.assign(this.event, responseJSON.event);
        this.clearGenerated();
        return responseJSON;
      }
    } catch (e) {
      evite.error('Error saving %O', e);
      Raven.captureException(e);
      if (this.saving) {
        this.saving = false;
        await controller.hideLoading();
      }
      await controller.showError('Error', e.message || defError);
      throw e;
    }
  };

  getRenderedImageUrl = async () => {
    const response = await evite.fetch(`/plus/create/${this.event.id}/rendered-image-url/`);
    if (!response.ok) {
      return null;
    }

    const json = await response.json();
    return json.rendered_image_url;
  };

  rerenderEventImage = () => {
    // Re-render the event image.
    // Call this only if the event image has failed to load
    const failed_url = this.event.options.rendered_image_url || '';
    const body = {};
    if (failed_url) {
      body.failed_url = failed_url.split('=')[0]; // Remove the sizing parameter
    }
    return evite
      .fetch(evite.urls.get('ajax_render_event_image', {event_id: this.event.id}), {
        method: 'POST',
        body: JSON.stringify(body),
      })
      .then((response) => response.json())
      .then((data) => (this.event.options.rendered_image_url = data.url));
  };

  /**
   * todo real preview is needed but a bunch of backend stuff has to change
   */
  crappyPreview = async () => {
    const {controller} = this;
    const previewUrl = `/event/${this.event.id}?preview=1&close=1`;

    await controller.showLoading();

    try {
      await this.submit();

      const originalUrl = await this.getRenderedImageUrl();
      for (let count = 20; count > 0; count--) {
        const newUrl = await this.getRenderedImageUrl();
        if (newUrl === null) {
          await evite.sleep(750);
          continue;
        }

        if (newUrl !== originalUrl) {
          break;
        }
        await evite.sleep(750);
      }

      const eventOrCard = window.is_invite === false ? 'card' : 'event';
      await controller.hideLoading(true);
      controller.openDialog({
        centered: true,
        children: (
          <ConfirmDialog
            title={`Preview ${eventOrCard}`}
            message={`The preview of your ${eventOrCard} is now ready to be viewed.`}
            confirmText="Preview"
            confirmDataQaId="preview_modal__preview_button"
            cancelDataQaId="preview_modal__cancel_button"
            onConfirm={() => {
              window.open(previewUrl);
            }}
          />
        ),
      });
    } finally {
      await controller.hideLoading();
    }
  };

  @action
  preview = async () => {
    const valid = this.validateForm();
    if (!valid) return;

    const {controller} = this;
    await controller.possiblyDoModalLogin();

    if (controller.isPremium) return this.crappyPreview();

    this.saveDraft('preview').then(() => {
      const data = this.getSubmitData();
      window.evite
        .fetch('/ajax/event/preview/', {
          method: 'post',
          dataType: 'json',
          body: JSON.stringify(data),
          credentials: 'include',
          cache: 'no-cache',
          headers: {
            'X-CSRFToken': evite.cookie('csrftoken'),
            Accept: 'application/json',
          },
        })
        .then((response) => {
          this.errorMessage = response.statusText;
          if (!response.ok) {
            throw response; // to be dealt with in catch(), below
          }
          return response.json();
        })
        .then((json) => {
          const {next, key} = json;
          evite.info(`got next: ${next}`);
          controller.openDialog({
            centered: true,
            children: (
              <ConfirmDialog
                title="Preview Event"
                message="The preview of your event is now ready to be viewed."
                confirmText="Preview"
                confirmDataQaId="preview_modal__preview_button"
                cancelDataQaId="preview_modal__cancel_button"
                onConfirm={() => {
                  window.open(next);
                  this._updateLocationIfNeeded();
                }}
                onCancel={() => {
                  this._updateLocationIfNeeded();
                }}
              />
            ),
          });
        })
        .catch((response) => {
          response
            .json()
            .then((json) => {
              const body = 'There was a problem with your preview. Please try again.';
              new ModalAjaxError({
                title: 'Error',
                body,
                error: new Error(json.statusText),
              }).show();
            })
            .then(() => {
              this._updateLocationIfNeeded;
            });
        });
    });
  };

  _updateLocationIfNeeded = () => {
    // Now that the event has definitely been saved, update the browser location if needed
    // to reflect the fact that we are now "editing a draft event" rather than still "creating a new event"
    const needsLocationUpdate = window.location.pathname.startsWith('/event/create/');
    if (needsLocationUpdate) {
      window.location = addParamsToUrl(urls.get('edit', {event_id: this.event.id}), {
        previous: 'guests',
      });
    }
  };

  /**
   * event options rsvp styles validation
   */
  @action
  validateRsvpStyles = () => {
    // special case https://evitetracking.atlassian.net/browse/EVT-10305
    // if all of the rsvp styles aren't filled out, set to default style
    const rsvp = this.event.rsvp_style;
    if (!rsvp.who || !rsvp.yes || !rsvp.maybe || !rsvp.no) {
      const style = RSVP_STYLES.default;
      rsvp.type = 'default';
      rsvp.who = style.who;
      rsvp.yes = style.yes;
      rsvp.maybe = style.maybe;
      rsvp.no = style.no;
    }

    return true;
  };

  /**
   * require custom_image_id if the template is uyo
   */
  premiumUyoIsValid = () => {
    if (this.controller.isPremium) {
      const {version} = this.event.options.design;
      const currVersion = this.templateStore.getVersion(version);
      if (
        currVersion &&
        currVersion.userImage.allowed &&
        !this.event.options.design.custom_image_key
      )
        return false;
    }
    return true;
  };

  /**
   * require custom_image_id if the template is uyo and prompt to fix if not set.
   */
  validateUyo = () => {
    if (this.controller.isPremium) {
      if (this.controller.step === 'design' && !this.premiumUyoIsValid()) {
        const onUpload = (e) => {
          if (this.hideValueProp) {
            this.hideValueProp();
          }
          if (this.mainImageController && this.mainImageController.onImageChange) {
            this.mainImageController.onImageChange(e);
          }
        };
        const onCancel = () => {
          const fakeInput = document.getElementById('fakeImageInput');
          fakeInput.removeEventListener('change', onUpload);
          return false;
        };

        if (this.mainImageController.cropping) {
          const dialog = new PopupModal({
            title: 'Save Image',
            buttons: ['Cancel', 'Save Image'],
            body: 'Do you want to save this image?',
            width: utils.isMobile() ? '100%' : '360px',
            onResponse: (response) => {
              if (response.target.innerHTML === 'Save Image') {
                this.mainImageController.onCrop();
              } else {
                this.controller.uyoError = true;
              }
            },
          });
          dialog.show();
          return false;
        }
        const buttonMarkup = `<button class="button-primary-inverse secondary-button" data-name="Cancel" data-index="${0}">Cancel</button>
            <label for="fakeImageInput" style="margin-left:10px" class="primary-button button-primary">Upload Image</label>
            <input style="display:none" id="fakeImageInput" type="file" accept="image/*"/>`;

        new PopupModal({
          title: 'Custom Image',
          buttonMarkup,
          buttons: ['Cancel'],
          body: 'Please upload a photo before continuing.',
          width: utils.isMobile() ? '100%' : '360px',
          onCancel: () => onCancel(),
        }).show();
        const fakeInput = document.getElementById('fakeImageInput');
        fakeInput.addEventListener('change', onUpload);
        return false;
      }
    } else if (this.template.uyo) {
      if (!this.event.options.custom_image_id) {
        // If this is true, then one of two scenarios has happened:
        // It's the initial event create flow, and no image was uploaded.
        // It's an existing event, and the host has edited the template from non-UYO to UYO.
        // Hopefully these two checks cover all the bases.
        if (
          !this.controller.anonDyoImageKey && // could be true if template changed
          !this.controller.anonDyoImagePath
        ) {
          this.setFormError('Please upload a photo first by tapping on the camera');
          return false;
        }
      }
    }
    return true;
  };

  validateCohost = () => {
    const nameField = this.formStatus.fields.cohost_name1;
    const emailField = this.formStatus.fields.cohost_email1;
    const required =
      this.formStatus.cohostVisible && Boolean(this.event.cohost_name1 || this.event.cohost_email1);
    if (!required) return true;

    if (!this.event.cohost_name1) {
      nameField.fieldStatus.error = 'Cohost name is required (or you can remove the Co-host)';
      return false;
    }
    if (!this.event.cohost_email1) {
      emailField.fieldStatus.error = 'Cohost email is required (or you can remove the Co-host)';
      return false;
    }
    const result = valid_email(this.event.cohost_email1);
    if (!result.is_valid) {
      emailField.fieldStatus.error = result.error_message;
      return false;
    }

    return true;
  };

  validateRegistries = () => {
    // reset errors before validating
    this.formStatus.registry_name_error = false;
    this.formStatus.registry_urls_error = false;

    const {gift_registry_name: name, gift_registry_urls: urlz} = this.event;

    if (urlz?.length && (name === null || name.trim() === '')) {
      this.formStatus.registry_name_error = true;
      return false;
    }

    if (name?.length && (urlz?.length < 1 || !urlz?.every(isValidURL))) {
      this.formStatus.registry_urls_error = true;
      return false;
    }

    return true;
  };

  @action
  setFormError(message) {
    this.formStatus.error = message;
    // trigger event so others know of the error
    // if (message)
    //   this.dispatchEvent(
    //     // new CustomEvent('validated', {detail: {valid: false}})
    //   );
  }

  @action
  addCohost = (focusCohost = false) => {
    this.formStatus.cohostVisible = true;
    if (focusCohost) {
      setTimeout(() => {
        document.getElementById('id_cohost_name1').focus();
      }, 0);
    }
  };

  @action
  removeCohost = () => {
    this.formStatus.cohostVisible = false;
    this.event.host_ids = [this.event.host_id];
    this.event.pending_host_ids = [];
    this.event.cohost_name1 = null;
    this.event.cohost_email1 = null;
  };

  @action
  addEndTime = () => {
    if (this.event.end_datetime) {
      this.formStatus.endTimeVisible = true;
      return;
    }

    // TODO: Unify this logic, may require some reorg -- get rid of Combopicker & Widget or else change mobile
    const startdate = this.event.start_datetime;
    // Mobile has 2 fields, desktop 1 (combo).
    if (this.formStatus.fields.start_datetime) {
      this.formStatus.fields.start_datetime.validate();
      if (!this.formStatus.fields.start_datetime.isValid) {
        return;
      }
    } else if (this.formStatus.fields.start_date) {
      this.formStatus.fields.start_date.validate();
      this.formStatus.fields.start_time.validate();
      if (
        !this.formStatus.fields.start_date.isValid ||
        !this.formStatus.fields.start_time.isValid
      ) {
        return;
      }
    }
    const field = 'end_datetime';
    const startPlusThree = new XDate(startdate).addHours(3);
    this.event[field] = startPlusThree.toString('i').slice(0, 16);
    this.formStatus.endTimeVisible = true;
    this.onFieldChange(field); // Make this field dirty
  };

  @action
  removeEndTime = () => {
    this.formStatus.endTimeVisible = false;
    this.event.end_datetime = null;
    delete this.formStatus.fields.end_datetime;
  };

  /**
   * set the start from from an date value
   * @param date
   */
  @action
  setStartDateTime = (date) => {
    if (!date) {
      this.event.start_datetime = null;
      return;
    }
    const dateStr = date.toString('i');

    // make sure the previously stored values are only
    // a max of 16 chars long
    this.event.start_datetime = dateStr.slice(0, 16);
  };

  /**
   * set the end from from an date value
   * @param date
   */
  @action
  setEndDateTime = (date) => {
    if (!date) {
      this.event.end_datetime = null;
      return;
    }
    this.addEndTime();

    const dateStr = date.toString('i');

    // make sure the previously stored values are only
    // a max of 16 chars long
    this.event.end_datetime = dateStr.slice(0, 16);
  };

  @action
  setTimeZone = (fullZone) => {
    this.event.known_timezone_name = fullZone;
  };

  @action
  updateGiftRegistryName = (name) => {
    this.event.gift_registry_name = name;
    if (name) {
      this.enableSponsoredRegistry(false);
    } else {
      this.enableSponsoredRegistry(true);
    }
    this.formStatus.isDirty = true;
  };

  @action
  removeAllGiftRegistries = () => {
    this.event.gift_registry_urls = [];
    this.formStatus.isDirty = true;
  };

  @action
  removeGiftRegistry = (index) => {
    this.event.gift_registry_urls.splice(index, 1);
    this.formStatus.isDirty = true;
    return this.event.gift_registry_urls;
  };

  @action
  addGiftRegistryURL = (registry_url, index) => {
    if (!this.event.gift_registry_urls) {
      this.event.gift_registry_urls = [registry_url];
    } else {
      this.event.gift_registry_urls[index] = registry_url;
    }
    this.formStatus.isDirty = true;
  };

  hasSendOnDateTime = () => Boolean(this.event.send_on);

  @action
  setSendOnDateTime = (dateTimeStr) => {
    if (this.event.send_on === dateTimeStr) return; // nothing to do

    // dateTimeStr should either be null (to clear it)
    // or a date/time string like "2020-02-04T00:00"
    // make sure it's one of those before proceeding
    const invalidStrMessage = 'Invalid date/time string for send_on';
    if (dateTimeStr !== null) {
      if (dateTimeStr.constructor.name !== 'String') throw invalidStrMessage;
      if (!Date.parse(dateTimeStr)) throw invalidStrMessage;
    }

    this.event.send_on = dateTimeStr;
    this.formStatus.isDirty = true;
  };

  @action
  clearSendOnDateTime = () => {
    if (this.event.send_on === null) return; // nothing to do

    this.setSendOnDateTime(null);
  };

  /**
   * useful for testing locally
   */
  addTestAmazonWishlist = () => {
    this.addWishlist({
      msgType: 'attach',
      listName: 'User&#39;s Wish List',
      listType: 'wishlist',
      listLink: 'https://www.amazon.com/gp/registry/wishlist/30TWG7VH4MCQH/ref=wl_attach_Evite',
      listId: '30TWG7VH4MCQH',
    });
  };

  shouldEnableGifting = () =>
    !this.getPledgelingId() &&
    this.event.pledgeling_beneficiary_type !== 'Crowdfund' &&
    !this.event.gift_registry_name;

  @action
  addVideoId = (video_id) => {
    this.event.event_videos_id = video_id;
    this.formStatus.isDirty = true;
  };

  removeVideo = () => {
    this.event.event_videos_id = null;
    this.formStatus.isDirty = true;
  };

  @action
  addWishlist = (data) => {
    this.event.wishlist = data;
    this.enableSponsoredRegistry(false);
    this.formStatus.isDirty = true;
  };

  @action
  removeWishlist = () => {
    this.event.wishlist = null;
    this.enableSponsoredRegistry(true);
    this.formStatus.isDirty = true;
  };

  getPledgelingId = () => {
    const ret = this.event.pledgeling_id;
    if (!ret && this.event.pledgeling_data && this.event.pledgeling_data.Organization) {
      return this.event.pledgeling_data.Organization.pledgeling_id;
    }

    return ret;
  };

  getPledgelingUuid = () => {
    if (this.event.pledgeling_data && this.event.pledgeling_data.Organization) {
      return this.event.pledgeling_data.Organization.pledgeling_uuid;
    }
    return null;
  };

  getEventId = () => this.event.id;

  @action
  setPledgelingId = (val) => {
    this.event.pledgeling_id = val;
    this.formStatus.isDirty = true;
  };

  @action
  setPledgelingData = (jsonPayload, type) => {
    if (type) {
      this.event.pledgeling_data[type] = jsonPayload;
      this.formStatus.isDirty = true;
    }
  };

  getPledgelingData = () => this.event.pledgeling_data;

  @action
  setCrowdFunding = (beneficiary, beneficiaryType, purpose, category) => {
    this.event.pledgeling_beneficiary = beneficiary;
    this.event.pledgeling_beneficiary_type = beneficiaryType;
    this.event.pledgeling_purpose = purpose;
    this.event.pledgeling_category = category;
    this.formStatus.isDirty = true;
  };

  @action
  clearPledgeling = (type = null) => {
    this.setPledgelingId(null);
    this.setPledgelingData(null, type);
    this.setPledgelingGoal(null, type);
    this.setCrowdFunding(null, null, null, null);
    this.formStatus.isDirty = true;
  };

  @action
  setPledgelingGoal = (val, type = null) => {
    if (type) {
      this.event.pledgeling_data[type].pledgeling_goal = String(val);
      this.formStatus.isDirty = true;
    } else this.event.pledgeling_goal = String(val);

    this.formStatus.isDirty = true;
  };

  @action
  disableGifting = () => {
    this._enableGiftingAndSponsoredRegistry(false);
    this.formStatus.isDirty = true;
  };

  @action
  enableGifting = (enable = true) => {
    if (enable) {
      enable = this.shouldEnableGifting();
    }
    this._enableGiftingAndSponsoredRegistry(enable);
    this.formStatus.isDirty = true;
  };

  @action
  enableSponsoredRegistry = (enable) => {
    if (enable) {
      enable = this.shouldEnableGifting();
    }
    this._enableGiftingAndSponsoredRegistry(enable);
    this.formStatus.isDirty = true;
  };

  // For EVT-11013 we need to tie together the sponsored registry and gifting
  // options, so when the user changes one, change the other to be the same.
  _enableGiftingAndSponsoredRegistry = (enable) => {
    this.event.options.event_option_show_gifting = enable;
    this.event.options.event_option_enable_sponsored_registry = enable;
    this.formStatus.isDirty = true;
  };

  @action
  setImageId = (image_id) => {
    this.event.options.custom_image_id = image_id;
  };

  @action
  setPremiumCustomImageId = (image_id) => {
    this.event.options.design.custom_image_key = image_id;
  };

  @action
  setPremiumCustomStampImageUrl = (url) => {
    this.event.options.design.custom_stamp = url;
  };

  @action
  setCustomLogo = (logo) => {
    const {image_id, image_url, desktop, mobile} = logo;
    const {x, y, width} = desktop;

    this.event.custom_logo = {
      image_id,
      image_url,
      desktop: {
        x,
        y,
        width,
      },
    };

    // For Free, we need separate sets for desktop and mw
    if (!this.controller.isPremium) {
      const {x, y, width} = mobile;
      this.event.custom_logo.mobile = {
        x,
        y,
        width,
      };
    }
  };

  @action
  addCustomLogo = (logo = null) => {
    this.event.custom_logo = logo;
  };

  @action
  removeCustomLogo = () => {
    this.event.custom_logo = null;
  };

  @action
  removeCustomURL = () => {
    this.event.custom_shareable_link = null;
  };

  @action
  addReminder2 = () => {
    this.formStatus.reminder2Visible = true;
  };

  @action
  removeReminder2 = () => {
    this.event.event_reminder_1 = null;
    this.formStatus.reminder2Visible = false;
  };

  @action
  removeRegistry = (registry_id) => {
    const {registries} = this.event;
    for (let i = 0; i < registries.length; i++) {
      if (registries[i].registry_id === registry_id) registries.splice(i, 1);
    }
  };

  /**
   * Called from the FormFieldModel. Used to implement a form dirty check
   * @param field
   */
  onFieldChange = (field) => {
    if (DIRTY_FIELDS.indexOf(field) > -1) {
      this.formStatus.isDirty = true;
    }
  };

  /*
   * Simple event save method when 'guest_options' experiment is active.
   * */
  @action
  submitForm = (status, onSuccess) => {
    const valid = this.validateForm();
    if (!valid) {
      return;
    }
    const data = this.getSubmitData();
    if (data.event) data.event.status = status;
    else data.status = status;
    this.saveForm(data, onSuccess);
  };

  /**
   * On the design step we create a draft for premium (and sometimes
   * in some other places) that are considered incomplete events.
   *
   * Validation for these events is suspended until this flag is on,
   * then it is considered user data. Mostly for the write step on premium
   * to know when to clear the fake draft start date and prompt the user.
   */
  @action
  setUserSaved = (on = true) => {
    this.event.user_saved = on;
  };

  startTimeAdded = true;

  validateStartDate = (value) => {
    const now = new XDate();
    if (!value) return null; // this will be required anyhow

    this.startTimeAdded = value.length >= 16;

    const start = new XDate(value.substring(0, 10));
    if (!start) return null;

    if (now.diffDays(start) <= -1) return PAST_ERROR;

    const valueEnd = this.event.end_datetime;
    if (valueEnd && this.formStatus.fields.end_datetime) {
      this.formStatus.fields.end_datetime.validate();
    }

    if (value === 'Invalid Date') return 'Please enter a valid start date';
    return null;
  };

  validateStartTime = (value) => {
    if (!value) return null; // allow require validator to handle
    const timePart = value.split('T');
    const timeComplete = timePart.length === 2 && timePart[1].length === 5;
    if (!timeComplete || !timePart[1].match('[0-9]{2}:[0-9]{2}')) {
      return 'Please enter a valid start time';
    }
    return null;
  };

  validateStartBeforeEndDate = (value) => {
    const valueStart = this.event.start_datetime;
    if (!value) return null;
    // if the last character is T, just return since the time field will be required anyhow
    if (value.charAt(value.length - 1) === 'T') return null;

    if (!valueStart) return null;

    const start = new XDate(valueStart);
    if (!start) return null; // validated on the start field

    const end = new XDate(value);
    if (!end) return null;
    if (start.diffSeconds(end) <= 0) return PAST_BEFORE_START_ERROR;

    if (end === 'Invalid Date') return 'Please enter a valid end Date / Time';

    return null;
  };

  @action
  clearGenerated() {
    if (this.event.title === UNTITLED_EVENT || this.event.title === UNTITLED_CARD) {
      this.event.title = '';
    }
    // this may be a copy, user_saved may be undefined or null. but if false the
    // event times should not be used
    if (this.event.user_saved === false) {
      this.event.start_datetime = '';
      this.event.end_datetime = '';
    }
  }

  @computed
  get isSponsoredTemplate() {
    const currentTemplateName = this.templateStore.template.name || '';
    return currentTemplateName.split('_')[0] === 'cst';
  }

  get shouldShowEviteProUpsell() {
    return this.isProUpsellEnabled && !this.isSponsoredTemplate;
  }

  get shouldShowLogoUploader() {
    if (this.controller.isPremium) return true;
    return this.isProUpsellEnabled && !this.isSponsoredTemplate;
  }

  @computed
  get isUpgrade() {
    // what to return for premium?
    if (this.controller.isPremium) return true;

    return (
      this.formStatus._isUpgrade ||
      this.paymentStore.hasSelectedOffer ||
      this.paymentStore.calc.previouslyPurchasedOffer
    );
  }

  @action
  willReceiveNewEventType = (newEventType) => {
    if (!newEventType) return;
    let shouldEnableGifting = true;
    if (newEventType === 'funeral_memorial_service') shouldEnableGifting = false;
    this.enableGifting(shouldEnableGifting);
  };

  @action
  userSelectedUpgrade = () => {
    this.formStatus._isUpgrade = true;
    this.event.options.downgraded = false;
  };

  @action
  userSelectedDownGrade = () => {
    this.formStatus._isUpgrade = false;
    this.event.options.downgraded = true;
    this.event.options.event_option_show_gifting = true;
  };

  isMealOptionsEventType = () => window.mealOptionsEventTypes.includes(this.event.event_type);

  @action
  openEditLogoView = () => {
    this.formStatus.isLogoEditMode = true;
  };

  @action
  closeLogoEditView = () => {
    this.formStatus.isLogoEditMode = false;
  };

  @action
  showEditLogoDialog = () => {
    const popUpModal = new PopupModal({
      buttons: ['Cancel', 'Edit Logo'],
      body: 'Would you like to edit the placement of your logo?',
      title: 'Edit Logo',
      onResponse: (response) => {
        if (response.target.innerHTML === 'Edit Logo') {
          this.openEditLogoView();
        }
        popUpModal.close();
      },
    });
    popUpModal.show();
  };
}
